import Pagination from "@/components/common/table/pagination";
import { useAuthorization } from "@/hooks/use-authorization";
import {
  useActivateRole,
  useDeleteRole,
  useGetPermissions,
  useGetRoles,
} from "@/queries/roles";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import type { RoleType } from "@/types/entities/roles";
import { Permissions } from "@/utils/permissions";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import DeactivateModal from "./deactivate-modal";
import DuplicateModal from "./duplicate-modal";
import TableHeader from "@/components/common/table/header";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";

const columnHelper = createColumnHelper<RoleType>();

const defaultFilters: (val: {
  status?: string;
  updated_at?: string;
}) => FilterOption[] = ({
  status = "active",
  updated_at = "",
}: {
  status?: string;
  updated_at?: string;
}) => [
  {
    id: 1,
    field: "updated_at",
    type: filterTypesEnum.dates,
    label: "Última actualización",
    shouldDisabledFutureDates: true,
    isUpdated: updated_at !== "",
    result: updated_at,
  },
  {
    id: 2,
    field: "status",
    type: filterTypesEnum.status,
    label: "Estado",
    isUpdated: true,
    result: status,
    status: [
      {
        field: "active",
        label: "Activo",
        value: status.includes("active"),
      },
      {
        field: "inactive",
        label: "Inactivo",
        value: status.includes("inactive"),
      },
    ],
  },
];

const columns = ({
  onCellButtonClick,
  onActivateDesactivateRole,
  onDuplicateRole,
  totalPermissions,
}: {
  onCellButtonClick: (id: string) => void;
  onActivateDesactivateRole: (role: RoleType) => void;
  onDuplicateRole: (id: string) => void;
  totalPermissions: number;
}) => {
  return [
    columnHelper.accessor("name", {
      header: "Nombre del rol",
      sortUndefined: "last",
      meta: {
        cellClassName: "group",
      },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        return (
          <TableLeadingCell
            title={info.getValue()}
            showButton
            onButtonClick={() => onCellButtonClick(info.row.original.id)}
          />
        );
      },
    }),
    columnHelper.accessor("members_count", {
      header: "Número de usuarios",
      sortUndefined: "last",
      meta: {
        className: "hidden lg:table-cell",
      },
      cell: (info) => {
        if (typeof info.getValue() !== "number") {
          return "";
        }
        return <p className="text-body font-normal">{info.getValue()}</p>;
      },
    }),
    columnHelper.display({
      header: "Estado",
      sortUndefined: "last",
      meta: {
        className: "hidden mdPlus:table-cell",
      },
      cell: (info) => {
        const original = info.row.original;
        if (!original.id) {
          return "";
        }
        return (
          <Badge
            variant={original.is_active ? "success" : "neutral"}
            shape="pill"
            className="h-6 text-sm leading-2xl"
          >
            {original.is_active ? "Activo" : "Inactivo"}
          </Badge>
        );
      },
    }),
    columnHelper.display({
      header: "Permisos otorgados",
      sortUndefined: "last",
      meta: {
        className: "hidden md:table-cell",
      },
      cell: (info) => {
        const original = info.row.original;
        if (!original.id) {
          return "";
        }
        const PermissionType = {
          editor: "Editor",
          reader: "Lector",
          admin: "Administrador",
        };
        return (
          <div className="flex items-center justify-between">
            <span>{PermissionType[original?.type] || "-"}</span>
            <Badge
              variant="neutral"
              shape="pill"
              className="h-6 text-sm leading-2xl"
            >
              {`${original.assigned_roles_count}/${totalPermissions}`}
            </Badge>
          </div>
        );
      },
    }),
    columnHelper.accessor("updated_at", {
      header: "Última actualización",
      sortUndefined: "last",
      meta: {
        className: "hidden xs:table-cell",
      },
      cell: (info) => {
        if (info.row.original.name) {
          if (!info.getValue()) {
            return (
              <span className="text-body leading-3xl font-normal">N/A</span>
            );
          }
          return (
            <span className="text-body leading-3xl font-normal">
              {dayjs(info.getValue()).format("MMM DD, YYYY | HH:mm A")}
            </span>
          );
        }

        if (!info.getValue()) {
          return "";
        }

        return <span className="text-body leading-3xl font-normal">N/A</span>;
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      enableSorting: false,
      meta: {
        className: "w-[56px]",
      },
      cell: (row) => {
        if (!row.row.original.name) {
          return "";
        }

        return (
          <div className="w-full flex justify-end">
            <Menu
              entity="rol"
              id={row.row.original.id}
              isActive={
                row.row.original.is_active !== undefined
                  ? row.row.original.is_active
                  : true
              }
              onActivateToggle={() =>
                onActivateDesactivateRole(row.row.original)
              }
              onDuplicate={() => onDuplicateRole(row.row.original.id || "")}
              isEditable={row.row.original.is_editable}
            />
          </div>
        );
      },
    }),
  ];
};

const Menu = ({
  entity,
  id,
  isEditable = true,
  isActive = true,
  // onDelete,
  onActivateToggle,
  onDuplicate,
}: {
  entity: string;
  id: string;
  isEditable?: boolean;
  isActive: boolean;
  // onDelete: () => void;
  onActivateToggle: () => void;
  onDuplicate: () => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.roles.update);
  const canDuplicate = hasPermission(Permissions.roles.duplicate);
  const canDelete = hasPermission(Permissions.roles.delete);
  const canRead = hasPermission(Permissions.roles.read);

  if (!canEdit && !canDelete && !canDuplicate && !isEditable && !canRead) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8 cursor-pointer">
          <Icon type="far" name="ellipsis-vertical" className="text-current" />
          <span className="sr-only">
            Acciones para {entity} {id}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral-silent text-body font-normal p-2"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {isEditable && canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink to={`/roles/${id}`} className="w-full">
                Editar rol
              </NavLink>
            </DropdownMenuItem>
          )}
          {!isEditable && canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink to={`/roles/${id}`} className="w-full">
                Ver rol
              </NavLink>
            </DropdownMenuItem>
          )}
          {canDuplicate && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={onDuplicate}
            >
              Duplicar rol
            </DropdownMenuItem>
          )}
          {isEditable && canDelete && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={onActivateToggle}
            >
              {`${isActive ? "Desactivar" : "Activar"} rol`}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            asChild
          >
            <NavLink to={`/roles/${id}?type=history`} className="w-full">
              Ver historial
            </NavLink>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Roles = () => {
  const { hasPermission } = useAuthorization();
  const { showToast } = useToast();
  const canCreateRoles = hasPermission(Permissions.roles.create);
  const canEditRoles = hasPermission(Permissions.roles.update);
  const canDeleteRoles = hasPermission(Permissions.roles.delete);
  const canDuplicate = hasPermission(Permissions.roles.duplicate);

  const [sorting, setSorting] = useState<SortingState>([]);
  const navigate = useNavigate();
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [page, setPage] = useQueryState("page", parseAsInteger);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("name"),
  );
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault("active"),
  );
  const [updated_at, setUpdatedAt] = useQueryState(
    "updated_at",
    parseAsString.withDefault(""),
  );

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>(
    defaultFilters({
      status,
      updated_at,
    }),
  );

  const [modalState, setModalState] = useState<
    "idle" | "duplicate" | "filters"
  >("idle");
  const [roleToDuplicate, setRoleToDuplicate] = useState<string | null>(null);
  const [roleToDesactivate, setRoleToDesactivate] = useState<RoleType | null>(
    null,
  );
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  const { data: permissions, isFetching: isLoadingPermissions } =
    useGetPermissions();

  const { data: roles, isFetching } = useGetRoles({
    ...(deferredQuery ? { search: deferredQuery } : {}),
    ...(order_by
      ? {
          order_by,
        }
      : undefined),
    page: page || 1,
    per_page: 20,
    filter: {
      ...(status ? { status } : {}),
      ...(updated_at ? { updated_at } : {}),
    },
  });

  useEffect(() => {
    if (roles) {
      if (roles.meta.current_page !== 1 && roles.data.length === 0) {
        if (roles.meta.current_page - 1 <= roles.meta.last_page) {
          setPage(roles.meta.current_page - 1);
          return;
        }
        setPage(1);
      }
    }
  }, [page, roles, setPage]);

  const shouldShowSkeleton = !roles || isLoadingPermissions || isLoadingUpdate;

  const { mutateAsync: mutateAsyncDelete } = useDeleteRole();
  const { mutateAsync: mutateAsyncActivate } = useActivateRole();

  const handleActivateDesactivateRole = useCallback(async () => {
    if (!roleToDesactivate) return;
    setIsLoadingUpdate(true);
    if (roleToDesactivate.is_active) {
      try {
        await mutateAsyncDelete(roleToDesactivate.id);
        showToast({
          title: "Rol desactivado",
          description: "El rol ha sido desactivado correctamente.",
          options: {
            type: "success",
            duration: 5000,
          },
        });
      } catch {
        showToast({
          title: "Error al desactivar el rol",
          description: "Ha ocurrido un error al desactivar el rol.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    } else {
      try {
        await mutateAsyncActivate(roleToDesactivate.id);
        showToast({
          title: "Rol activado",
          description: "El rol ha sido activado correctamente.",
          options: {
            type: "success",
            duration: 5000,
          },
        });
      } catch {
        showToast({
          title: "Error al activar el rol",
          description: "Ha ocurrido un error al activar el rol.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    }
    setPage(1);
    setRoleToDesactivate(null);
    setIsLoadingUpdate(false);
  }, [
    roleToDesactivate,
    setPage,
    mutateAsyncDelete,
    showToast,
    mutateAsyncActivate,
  ]);

  const handleSetDeactivateRole = (role: RoleType) => {
    setRoleToDesactivate(role);
  };

  const columnsData = useMemo(() => {
    const _columns = columns({
      onActivateDesactivateRole: handleSetDeactivateRole,
      onDuplicateRole: (roleId: string) => {
        setRoleToDuplicate(roleId);
        setTimeout(() => {
          setModalState("duplicate");
        }, 100);
      },
      onCellButtonClick: (id: string) => {
        navigate(`/roles/${id}`);
      },
      totalPermissions: permissions?.length || 0,
    }) as ColumnDef<RoleType>[];
    return _columns.filter((column) => {
      if (column.id === "actions") {
        return canEditRoles || canDeleteRoles || canDuplicate;
      }
      return true;
    });
  }, [permissions, canDeleteRoles, canEditRoles, canDuplicate, navigate]);

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      defaultFilters({
        status: "active",
        updated_at: "",
      }),
    );
    setStatus(null);
    setUpdatedAt(null);
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(f.result);
        } else if (f.field === "updated_at") {
          setUpdatedAt(f.result);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  return (
    <>
      {roleToDuplicate !== null && (
        <DuplicateModal
          isOpen={modalState === "duplicate"}
          roleId={roleToDuplicate}
          onClose={() => setModalState("idle")}
        />
      )}
      {roleToDesactivate !== null && (
        <DeactivateModal
          isOpen={roleToDesactivate !== null}
          isActive={
            roleToDesactivate.is_active !== undefined
              ? roleToDesactivate.is_active
              : true
          }
          onClose={() => {
            setRoleToDesactivate(null);
          }}
          isLoading={false}
          onAccept={handleActivateDesactivateRole}
        />
      )}
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
      <div className="h-full flex flex-col gap-3">
        <p className="font-normal text-body leading-3xl">
          Administra los roles y permisos disponibles para los usuarios de SDT.
        </p>
        <div className="h-full min-h-[500px] relative">
          <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden">
            <TableHeader
              createLabel="Nuevo rol"
              search={searchInput}
              searchPlaceholder="Buscar rol..."
              setSearch={setSearchInput}
              title="Roles disponibles"
              isLoading={isFetching}
              canCreate={canCreateRoles}
              onCreate={() => {
                navigate("/roles/new");
              }}
              onFilter={() => setModalState("filters")}
              filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
            />
            <TableData<RoleType>
              wrapperClassName="min-h-0 flex-1 overflow-auto h-full relative"
              headerClassName="sticky top-0 bg-neutral-object z-20 border-b border-neutral"
              columns={columnsData}
              data={roles?.data || []}
              sorting={sorting}
              onSortingChange={(val) => {
                setPage(1);
                setSorting(val);
                if (val.length === 0) {
                  setOrderBy("name");
                  return;
                }
                setOrderBy(
                  val.length > 0
                    ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
                    : "name",
                );
              }}
              isFirstLoading={shouldShowSkeleton}
              stripped
            />
            <div className="p-3">
              <Pagination
                totalPages={roles?.meta?.last_page || 0}
                totalItems={roles?.meta?.total || 0}
                pageSize={roles?.data.length || 0}
                currentPage={roles?.meta?.current_page || 1}
                hasNext={
                  (roles?.meta?.current_page || 0) <
                  (roles?.meta?.last_page || 0)
                }
                hasPrevious={(roles?.meta?.current_page || 0) > 1}
                onPageChange={(page) => {
                  setPage(page);
                }}
                onNext={() => {
                  if (roles?.meta?.current_page && roles?.meta?.last_page) {
                    if (roles.meta?.current_page < roles.meta?.last_page) {
                      setPage(roles.meta?.current_page + 1);
                    }
                  }
                }}
                onPrevious={() => {
                  if (roles?.meta?.current_page && roles?.meta?.last_page) {
                    if (roles.meta?.current_page > 1) {
                      setPage(roles.meta?.current_page - 1);
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Roles;
